import { environment } from '../../environments/environment';

/**
 * Cross-host helpers for the marketing-site / Quine-app split. The two origins come from
 * the active environment (env.config.ts) — a real subdomain pair in prod, two Firebase
 * Hosting site URLs in dev/stage, and quine.localhost/localhost locally. Nothing here is
 * hardcoded to a specific host anymore.
 *
 * Redirects fire only when you're on the *other* known host, so unknown hosts (a `ng serve`
 * on localhost while pointed at a deployed API, Firebase preview channels, etc.) are left
 * alone and serve both halves without bouncing.
 */

function hostnameOf(origin: string): string {
  try {
    return new URL(origin).hostname;
  } catch {
    return '';
  }
}

function currentHostname(host?: string): string {
  return host ?? (typeof window !== 'undefined' ? window.location.hostname : '');
}

/** True when the current (or given) host is the configured Quine app host. */
export function isQuineHost(host?: string): boolean {
  return currentHostname(host) === hostnameOf(environment.appOrigin);
}

/** True when the current (or given) host is the configured marketing host. */
export function isMarketingHost(host?: string): boolean {
  return currentHostname(host) === hostnameOf(environment.marketingOrigin);
}

/**
 * Whether marketing and app live on different hosts. When false (single-host envs like
 * dev's one *.web.app site), the host guards must NOT cross-redirect — both halves are
 * served from the same domain, and redirecting would loop.
 */
export function hasSeparateHosts(): boolean {
  return hostnameOf(environment.marketingOrigin) !== hostnameOf(environment.appOrigin);
}

export function getMarketingOrigin(): string {
  return environment.marketingOrigin;
}

export function getQuineOrigin(): string {
  return environment.appOrigin;
}

export function redirectToMarketing(path: string = '/'): void {
  if (typeof window === 'undefined') return;
  window.location.href = `${getMarketingOrigin()}${path}`;
}

export function redirectToQuine(path: string = '/v2/dashboard'): void {
  if (typeof window === 'undefined') return;
  window.location.href = `${getQuineOrigin()}${path}`;
}

export function getQuineLoginUrl(): string {
  return `${getQuineOrigin()}/v2/login`;
}
